import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Shield, AlertCircle, CheckCircle, Clock, Building2, 
  ChevronRight, AlertTriangle, RefreshCw, ExternalLink 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import PropertyCompliance from '../components/PropertyCompliance';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

// Static color class maps for Tailwind compilation
const textColorMap = {
  green: 'text-green-600',
  yellow: 'text-yellow-600',
  red: 'text-red-600',
  gray: 'text-gray-600',
  slate: 'text-slate-600',
  blue: 'text-blue-600',
};

export default function Compliance() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [complianceSummary, setComplianceSummary] = useState({});
  const [serviceOrders, setServiceOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Fetch properties
      const propertiesRes = await fetch(`${API_URL}/api/properties`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (propertiesRes.ok) {
        const propertiesData = await propertiesRes.json();
        setProperties(propertiesData);
        
        // Fetch compliance status for each property
        const summaryData = {};
        for (const prop of propertiesData) {
          try {
            const statusRes = await fetch(
              `${API_URL}/api/compliance/properties/${prop.id}/status`,
              { headers: { 'Authorization': `Bearer ${getToken()}` } }
            );
            if (statusRes.ok) {
              summaryData[prop.id] = await statusRes.json();
            }
          } catch (e) {
            console.error('Error fetching compliance for', prop.id);
          }
        }
        setComplianceSummary(summaryData);
        
        // Select first property by default
        if (propertiesData.length > 0) {
          setSelectedProperty(propertiesData[0].id);
        }
      }

      // Fetch service orders
      const ordersRes = await fetch(`${API_URL}/api/compliance/service-orders`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setServiceOrders(ordersData.orders || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const getPropertyStatus = (propertyId) => {
    const summary = complianceSummary[propertyId];
    if (!summary) return { status: 'unknown', color: 'gray' };
    
    if (summary.is_compliant) {
      return { status: 'Compliant', color: 'green', icon: CheckCircle };
    } else if (summary.expired.length > 0) {
      return { status: 'Expired', color: 'red', icon: AlertTriangle };
    } else if (summary.missing_mandatory.length > 0) {
      return { status: 'Incomplete', color: 'yellow', icon: AlertCircle };
    }
    return { status: 'Review', color: 'yellow', icon: Clock };
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Property Compliance</h1>
          <p className="text-gray-600">
            Manage certificates per property. All mandatory documents must be valid before signing tenancies.
          </p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {Object.values(complianceSummary).filter(s => s.is_compliant).length}
                </p>
                <p className="text-sm text-gray-500">Compliant Properties</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {Object.values(complianceSummary).filter(s => s.expired?.length > 0).length}
                </p>
                <p className="text-sm text-gray-500">With Expired Certs</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {Object.values(complianceSummary).reduce((acc, s) => acc + (s.expiring_soon?.length || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Expiring Soon</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{serviceOrders.filter(o => o.status === 'pending').length}</p>
                <p className="text-sm text-gray-500">Pending Orders</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Property List */}
          <div className="lg:col-span-1">
            <Card className="p-4">
              <h2 className="font-bold mb-4">Your Properties</h2>
              
              {properties.length === 0 ? (
                <p className="text-gray-500 text-sm">No properties found</p>
              ) : (
                <div className="space-y-2">
                  {properties.map((property) => {
                    const status = getPropertyStatus(property.id);
                    const StatusIcon = status.icon || Shield;
                    const summary = complianceSummary[property.id];
                    
                    return (
                      <button
                        key={property.id}
                        onClick={() => setSelectedProperty(property.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selectedProperty === property.id
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="font-medium text-sm">
                                {property.address?.line1}
                              </p>
                              <p className="text-xs text-gray-500">
                                {property.address?.city}, {property.address?.postcode}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {summary && (
                              <span className="text-xs font-medium">
                                {summary.compliance_percentage}%
                              </span>
                            )}
                            <StatusIcon className={`w-4 h-4 ${textColorMap[status.color] || 'text-slate-600'}`} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Service Orders */}
            {serviceOrders.length > 0 && (
              <Card className="p-4 mt-4">
                <h2 className="font-bold mb-4">Recent Orders</h2>
                <div className="space-y-2">
                  {serviceOrders.slice(0, 5).map((order) => (
                    <div 
                      key={order.id}
                      className="p-3 bg-gray-50 rounded-lg text-sm"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">
                          {order.certificate_type?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getOrderStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs">
                        £{order.cost} • {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Property Compliance Detail */}
          <div className="lg:col-span-2">
            {selectedProperty ? (
              <PropertyCompliance 
                propertyId={selectedProperty}
                onComplianceChange={(status) => {
                  setComplianceSummary(prev => ({
                    ...prev,
                    [selectedProperty]: status
                  }));
                }}
              />
            ) : (
              <Card className="p-8 text-center">
                <Shield className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Select a property to view compliance status</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
